import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { useIntegrationConfig } from "@/hooks/use-itsm-config";
import { useFreshserviceGroups } from "@/hooks/use-itsm-metadata";

interface Props {
  tenantId: string;
  credentialId: string;
}

/**
 * Read-only view of the credential's agent-group limit. The importer applies
 * that ITSM config setting at fetch time, so a job cannot override it.
 */
export function FreshserviceGroupScope({ tenantId, credentialId }: Props) {
  const config = useIntegrationConfig(tenantId, credentialId);
  const groupIds = config.data?.config.provider_config?.inbound_group_ids;
  const restricted = groupIds != null && groupIds.length > 0;
  const groups = useFreshserviceGroups(
    tenantId,
    restricted ? credentialId : undefined,
  );

  let body: ReactNode;
  if (config.isLoading) {
    body = (
      <p className="text-muted-foreground text-sm">Loading group limit...</p>
    );
  } else if (config.isError) {
    body = (
      <p className="text-muted-foreground text-sm">
        The ITSM config could not be loaded, so the group limit is unknown. The
        import still applies it.
      </p>
    );
  } else if (groupIds == null) {
    body = (
      <p className="text-muted-foreground text-sm">
        No group limit is set. This import fetches tickets and changes from
        every agent group.
      </p>
    );
  } else if (groupIds.length === 0) {
    body = (
      <p role="status" className="text-sm text-amber-600">
        The group limit is on with no groups selected, so this import fetches
        nothing. Select groups or turn off the limit in ITSM config.
      </p>
    );
  } else {
    const options = groups.data?.options ?? [];
    body = (
      <>
        <p className="text-muted-foreground text-sm">
          This import only fetches tickets and changes assigned to these
          groups. Unassigned records are skipped.
        </p>
        <ul className="flex flex-wrap gap-2" aria-label="Agent groups">
          {groupIds.map((id) => (
            <li key={id}>
              <Badge variant="secondary">
                {options.find((o) => o.value === String(id))?.label ??
                  `Group ${id}`}
                <span className="text-muted-foreground ml-1">{id}</span>
              </Badge>
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <section
      aria-labelledby="freshservice-group-scope-title"
      className="space-y-2 rounded-lg border p-3"
    >
      <div className="flex items-center justify-between gap-3">
        <h3
          id="freshservice-group-scope-title"
          className="text-sm font-medium"
        >
          Agent groups
        </h3>
        <Link
          to="/itsm-config"
          search={{ tenantId, credentialId }}
          className="text-muted-foreground hover:text-primary flex items-center gap-1 text-sm hover:underline"
        >
          Edit in ITSM config
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </Link>
      </div>
      {body}
    </section>
  );
}
